import { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ImageUploader = ({ images, onImagesChange }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const uploadToCloudinary = async (file) => {
    // Get signature from backend
    const sigResponse = await axios.get(`${BACKEND_URL}/api/cloudinary/signature?resource_type=image&folder=indwellerstay/properties`);
    const sig = sigResponse.data;

    // Upload directly to Cloudinary
    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', sig.api_key);
    formData.append('timestamp', sig.timestamp);
    formData.append('signature', sig.signature);
    formData.append('folder', sig.folder);

    const cloudinaryResponse = await axios.post(
      `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
      formData,
      {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      }
    );

    return cloudinaryResponse.data.secure_url;
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    const newImages = [...images];
    let successCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file size (max 10MB for Cloudinary free tier)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 10MB allowed.`);
        continue;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file.`);
        continue;
      }

      try {
        toast.info(`Uploading ${file.name}... (${i + 1}/${files.length})`);
        const imageUrl = await uploadToCloudinary(file);
        newImages.push(imageUrl);
        successCount++;
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} image(s) uploaded successfully!`);
    }

    onImagesChange(newImages);
    setUploading(false);
    setUploadProgress(0);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = async (index) => {
    const imageUrl = images[index];
    const newImages = images.filter((_, i) => i !== index);
    
    // Try to delete from Cloudinary if it's a Cloudinary URL
    if (imageUrl.includes('cloudinary.com')) {
      try {
        // Extract public_id from URL
        // URL format: https://res.cloudinary.com/{cloud_name}/image/upload/{version}/{public_id}.{format}
        const urlParts = imageUrl.split('/upload/');
        if (urlParts.length > 1) {
          let publicId = urlParts[1];
          // Remove version if present (v1234567890/)
          publicId = publicId.replace(/^v\d+\//, '');
          // Remove file extension
          publicId = publicId.replace(/\.[^/.]+$/, '');
          
          await axios.delete(`${BACKEND_URL}/api/cloudinary/delete/${encodeURIComponent(publicId)}`);
        }
      } catch (error) {
        console.error('Delete error:', error);
        // Continue anyway - image might already be deleted
      }
    }
    
    onImagesChange(newImages);
    toast.success('Image removed');
  };

  const handleAddUrl = () => {
    const url = prompt('Enter image URL:');
    if (url && url.trim()) {
      onImagesChange([...images, url.trim()]);
      toast.success('Image URL added');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id="image-upload"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="font-body"
        >
          {uploading ? (
            <>
              <Loader2 className="animate-spin mr-2" size={18} />
              Uploading... {uploadProgress}%
            </>
          ) : (
            <>
              <Upload size={18} className="mr-2" />
              Upload Images
            </>
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={handleAddUrl}
          className="font-body text-gray-600"
        >
          <ImageIcon size={18} className="mr-2" />
          Add URL
        </Button>
      </div>

      {uploading && (
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-brand-purple h-2 rounded-full transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
      )}

      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {images.map((url, index) => (
            <div key={index} className="relative group">
              <img
                src={url}
                alt={`Property ${index + 1}`}
                className="w-full h-24 object-cover rounded-lg border border-gray-200"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/150?text=Error';
                }}
              />
              <button
                type="button"
                onClick={() => handleRemoveImage(index)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                title="Remove image"
              >
                <X size={14} />
              </button>
              <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1 rounded">
                {index + 1}
              </div>
              {url.includes('cloudinary.com') && (
                <div className="absolute bottom-1 right-1 bg-green-500/80 text-white text-xs px-1 rounded">
                  ☁️
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {images.length === 0 && (
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
          <ImageIcon className="mx-auto mb-2 text-gray-400" size={32} />
          <p className="text-gray-500 font-body text-sm">
            No images yet. Upload images or add URLs.
          </p>
        </div>
      )}

      <p className="text-xs text-gray-500 font-body">
        Images are stored permanently on cloud. Supported: JPEG, PNG, GIF, WebP. Max 10MB per image.
      </p>
    </div>
  );
};
